import notify from "../../../lib/notify";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { confirmAbsence2, getPendingAbsences2, rejectAbsence2 } from "../../../api/absence2";
import { useAuth } from "../../../contexts/auth";
import { useCompanyContext } from "../../../contexts/company";
import type { PendingSlot2 } from "../types";

export function useAbsenceConfirmation(currentMonth: Date) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { companyId } = useCompanyContext();

  const [timeSlots, setTimeSlots] = useState<PendingSlot2[]>([]);
  const [hasNewData, setHasNewData] = useState(false);
  const [visiblePopupHoliday, setVisiblePopupHoliday] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");

  const departments = Array.from(
    new Set(timeSlots.map((s) => s.department).filter(Boolean)),
  ) as string[];

  const filteredSlots = timeSlots
    .filter((slot) =>
      `${slot.firstName} ${slot.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()),
    )
    .filter((slot) => (selectedDepartment ? slot.department === selectedDepartment : true));

  const fetchPendingAbsences = useCallback(async () => {
    const company = companyId === 0 ? user?.companyId || 0 : companyId;
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;
    try {
      const response = await getPendingAbsences2(company, year, month);
      if (response.isOk && response.data && response.data.length > 0) {
        setTimeSlots(
          response.data.map((item) => ({
            firstName: item.firstName ?? undefined,
            lastName: item.lastName ?? undefined,
            department: item.department ?? undefined,
            from: new Date(item.from),
            to: new Date(item.to),
            name: item.name,
            note: item.note ?? undefined,
            id: item.id,
            employeeId: item.employeeId,
            companyId: item.companyId,
          })),
        );
        setHasNewData(true);
      } else {
        setTimeSlots([]);
        setHasNewData(true);
      }
    } catch {
      setTimeSlots([]);
      setHasNewData(false);
    }
  }, [companyId, user?.companyId, currentMonth]);

  const handleOpenConfirmPopup = useCallback(async () => {
    await fetchPendingAbsences();
    setVisiblePopupHoliday(true);
  }, [fetchPendingAbsences]);

  const handleConfirmAbsence = useCallback(
    async (id: number) => {
      const result = await confirmAbsence2(id);
      if (result.isOk) {
        setTimeSlots((prev) => prev.filter((s) => s.id !== id));
        notify(t("Absence confirmed sucessfull."), "success");
      } else {
        notify(`${t("Error:")} ${result.error || t("Unknown error")}`, "error");
      }
    },
    [t],
  );

  const handleRejectAbsence = useCallback(
    async (id: number) => {
      const result = await rejectAbsence2(id);
      if (result.isOk) {
        setTimeSlots((prev) => prev.filter((s) => s.id !== id));
      } else {
        notify(`${t("Error:")} ${result.error || t("Unknown error")}`, "error");
      }
    },
    [t],
  );

  const handleConfirmAll = useCallback(
    async (ids: number[]) => {
      await Promise.all(ids.map((id) => handleConfirmAbsence(id)));
      setTimeSlots((prev) => prev.filter((slot) => !ids.includes(Number(slot.id))));
      notify(t("AllAbsencesaccepted"), "success");
    },
    [handleConfirmAbsence, t],
  );

  const handleRejectAll = useCallback(
    async (ids: number[]) => {
      await Promise.all(ids.map((id) => handleRejectAbsence(id)));
      setTimeSlots((prev) => prev.filter((slot) => !ids.includes(Number(slot.id))));
    },
    [handleRejectAbsence],
  );

  return {
    timeSlots,
    filteredSlots,
    hasNewData,
    visiblePopupHoliday,
    setVisiblePopupHoliday,
    searchQuery,
    setSearchQuery,
    selectedDepartment,
    setSelectedDepartment,
    departments,
    handleOpenConfirmPopup,
    handleConfirmAbsence,
    handleRejectAbsence,
    handleConfirmAll,
    handleRejectAll,
    fetchPendingAbsences,
  };
}
